/**
 * Solvane scroll — deliberate in-page travel.
 *
 * WHY NOT `scroll-behavior: smooth`.
 * The CSS property works, but its duration is fixed by the browser and it is
 * fast — on a page this long, jumping from the root cause to the offer takes
 * about a third of a second and reads as a cut. The whole reason a reader is
 * sent down the page rather than straight to checkout is to let them see how
 * much is between the two points. A cut hides that; a glide shows it.
 *
 * So the duration scales with the distance travelled, with a floor and a
 * ceiling: short hops stay snappy, long ones take about a second and a
 * quarter. Never longer — past that it stops feeling considered and starts
 * feeling like the page is holding you hostage.
 *
 * The easing is a standard ease-in-out cubic. It starts slowly, which is what
 * makes the movement legible: the reader sees where they were before the page
 * picks up speed.
 *
 * `prefers-reduced-motion` falls straight through to a normal jump. Animated
 * scrolling is a known vestibular trigger and this is exactly the kind of long
 * travel that provokes it.
 */

(function () {
  'use strict';

  var MIN = 420;   // ms — a short hop should still feel instant
  var MAX = 1250;  // ms — beyond this it stops being a glide
  var PER_PX = 0.55;

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* The sticky header would otherwise cover the heading being scrolled to.
     Read from the live element rather than hard-coded, so it stays correct if
     the header ever changes height. */
  function headerOffset() {
    var h = document.querySelector('.sv-header');
    if (!h) return 16;
    var pos = getComputedStyle(h).position;
    return (pos === 'sticky' || pos === 'fixed') ? h.getBoundingClientRect().height + 16 : 16;
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  var running = null;

  function glide(toY) {
    var startY = window.scrollY;
    var delta = toY - startY;
    if (Math.abs(delta) < 2) return;

    var duration = Math.min(MAX, Math.max(MIN, Math.abs(delta) * PER_PX));
    var start = null;
    if (running) cancelAnimationFrame(running);

    function step(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      window.scrollTo(0, startY + delta * easeInOutCubic(t));
      if (t < 1) running = requestAnimationFrame(step);
      else running = null;
    }
    running = requestAnimationFrame(step);
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="#"]');
    if (!link) return;

    // Same-document links only. A hash on another page is a normal navigation.
    var url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || url.host !== window.location.host) return;
    if (!url.hash || url.hash === '#') return;

    var target = document.getElementById(url.hash.slice(1));
    if (!target) return;

    e.preventDefault();

    var y = target.getBoundingClientRect().top + window.scrollY - headerOffset();

    /* ⚠ A HIDDEN DOCUMENT GETS NO ANIMATION FRAMES. `requestAnimationFrame`
       does not tick while the tab is backgrounded or the window minimised, so
       `glide` would set the URL to the target and never move the page — the
       reader comes back to a page that says `#compare` and is still where they
       left it. Same fall-through as reduced motion: jump, don't glide. */
    if (reduced() || document.hidden) {
      window.scrollTo(0, y);
    } else {
      glide(y);
    }

    // Keep the URL and the focus ring honest — a scroll that does not move
    // focus leaves a keyboard user where they started.
    history.pushState(null, '', url.hash);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  // A user scrolling mid-glide has changed their mind. Stop immediately.
  ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
    window.addEventListener(evt, function () {
      if (running) {
        cancelAnimationFrame(running);
        running = null;
      }
    }, { passive: true });
  });
})();
